'use no memo';
import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {useState} from 'react';
import type {Active} from '@dnd-kit/core';
import {DragOverlay} from '@dnd-kit/core';
import {defaultDropAnimationSideEffects, DndContext, type DropAnimation, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {SortableContext} from '@dnd-kit/sortable';
import {noop} from '@Utils/general.util';

interface SlideshowListProps {
  id: string;
  className?: string;
  slidesOrder: string[];
  onDragEnd?(sourceIndex: number, destinationIndex: number): void;
  renderItem(item: string, index: number): ReactElement<any>;
}

export function SlideshowList({className = '', onDragEnd = noop, ...props}: SlideshowListProps): ReactElement<any> {
  const [active, setActive] = useState<Active | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );
  const activeItem = useMemo(() => props.slidesOrder.find((item) => item === active?.id), [active, props.slidesOrder]);
  const dropAnimationConfig: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0',
        },
      },
    }),
  };

  const getDraggableItems = (): ReactElement<any>[] => {
    return props.slidesOrder.map((slideId, index) => {
      return props.renderItem(slideId, index);
    });
  };

  const getDraggingItem = (): ReactElement<any> | null => {
    if (!activeItem) {
      return null;
    }
    return props.renderItem(activeItem, props.slidesOrder.indexOf(activeItem));
  };

  return (
    <div id={props.id} className={className}>
      <DndContext
        sensors={sensors}
        onDragStart={({active}): void => {
          setActive(active);
        }}
        onDragEnd={({active, over}): void => {
          if (!over) {
            return;
          }

          if (active.id === over.id) {
            return;
          }

          const sourceId = active.id;
          const destinationId = over.id;

          const sourceIndex = props.slidesOrder.findIndex((item) => {
            return item === sourceId;
          });
          const destinationIndex = props.slidesOrder.findIndex((item) => {
            return item === destinationId;
          });
          onDragEnd(sourceIndex, destinationIndex);
        }}
        onDragCancel={(): void => {
          setActive(null);
        }}
      >
        <SortableContext items={props.slidesOrder}>{getDraggableItems()}</SortableContext>
        <DragOverlay dropAnimation={dropAnimationConfig}>{getDraggingItem()}</DragOverlay>
      </DndContext>
    </div>
  );
}
